import { useFragment } from '@apollo/client';
import { FeatureFlag } from '@ps/web-core-enums';
import type { EventId } from '@ps/web-sports-api-types';
import React from 'react';

import useFeature from '@swf/core/hooks/useFeature';
import { useLiveData } from '@swf/sports/providers/LiveDataProvider';
import type { Participants } from '@swf/sports/types/event';
import type { EventTypeId } from '@swf/sports/types/eventType';
import type { Orientation } from '@swf/sports/types/liveData';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import { getIsEventTypeScoreboardEnabled } from '@swf/sports/utils/getIsEventTypeScoreboardEnabled';
import {
  getIsFootballScoreboard,
  getIsTennisScoreboard
} from '@swf/sports/utils/getScoreboardType';

import FootballScoreboard from './components/FootballScoreboard/FootballScoreboard';
import TennisScoreboard from './components/TennisScoreboard/TennisScoreboard';
import Unavailable from './components/Unavailable/Unavailable';

export interface Props {
  eventId: EventId;
  eventTypeId: EventTypeId;
  isGoingInPlay: boolean;
  orientation?: Orientation;
  participants: Participants | undefined;
  isValidInPlayStatus?: boolean;
}

const Scoreboard: React.FC<Props> = ({
  eventId,
  eventTypeId,
  isGoingInPlay,
  orientation = 'vertical',
  participants,
  isValidInPlayStatus
}) => {
  const { isLoading, isError } = useLiveData();
  const { data, complete, missing } = useFragment(getEventScoreboardFragment(eventTypeId, eventId));
  const isScoreboardsEnabled = useFeature(FeatureFlag.ISPScoreboards);
  const isEventTypeScoreboardEnabled = getIsEventTypeScoreboardEnabled(eventTypeId);

  if (!isScoreboardsEnabled || !isEventTypeScoreboardEnabled || !participants) {
    return null;
  }

  const isMissingData = missing || !complete;
  if (isGoingInPlay || isLoading || isError || isMissingData) {
    return (
      <Unavailable
        participants={participants}
        eventTypeId={eventTypeId}
        isLoading={isLoading}
        orientation={orientation}
        isValidInPlayStatus={isValidInPlayStatus}
      />
    );
  }

  if (getIsFootballScoreboard(eventTypeId, data)) {
    return (
      <FootballScoreboard
        participants={participants}
        footballFixture={data}
        orientation={orientation}
        isValidInPlayStatus={isValidInPlayStatus}
      />
    );
  }

  if (getIsTennisScoreboard(eventTypeId, data)) {
    return (
      <TennisScoreboard
        participants={participants}
        tennisMatch={data}
        isValidInPlayStatus={isValidInPlayStatus}
      />
    );
  }

  return null;
};

export default Scoreboard;
